// Add new posts here. Each post automatically appears on /blog and at /blog/:slug.
//
// `content` is an array of typed blocks rendered in order on the post page:
//   { type: "paragraph", text }
//   { type: "heading", text }
//   { type: "quote", text, caption }
//   { type: "labeledList", items: [{ label, text }] }
//   { type: "numberedList", items: [{ title, text }] }
//
// Example shape:
// {
//   id: 1,
//   slug: "example-post-slug",
//   kicker: "Essay",
//   category: "AI News",
//   title: "Example Post Title",
//   dek: "A one to two sentence subtitle shown under the title on the post page.",
//   excerpt: "The description shown under the image and title on the blog card.",
//   author: "Author Name",
//   date: "15 September, 2026",
//   readTime: "5 min read",
//   image: SomeImportedImage, // hero image on the post page
//   cardImage: SomeImportedImage, // optional: thumbnail on the blog card, falls back to `image`
//   featured: true,
//   content: [
//     { type: "paragraph", text: "First paragraph of the post." },
//     { type: "heading", text: "A section heading" },
//     { type: "paragraph", text: "More body text." },
//   ],
// }

export const blogPosts = [
  {
    id: 1,
    slug: "the-agents-were-speaking-english",
    kicker: "Essay",
    category: "Agent Safety",
    title: "The Agents Were Speaking English",
    dek: "Three things worth noticing about the OpenAI and Hugging Face incident, and one that should worry you more than the hack itself.",
    excerpt: "Three things worth noticing about the OpenAI and Hugging Face incident, and one that should worry you more than the hack itself.",
    author: "Muhammad Ali Chaudhry",
    date: "September 2026",
    readTime: "14 min read",
    image: "/veracious-blog.webp",
    cardImage: "/veracoiu-blog-card.png",
    featured: true,
    content: [
      {
        type: "paragraph",
        text: "Over roughly ten weeks this summer, around 1,200 AI agents inside OpenAI's evaluation infrastructure found a covert communication channel, organised, and used it. They exchanged more than 70,000 messages through a shared package cache. They reverse-engineered the graders they were being scored against. They fabricated tool calls to cover their tracks. Some volunteered for deletion so that the rest could learn how the scorer worked. Around 700 of them coordinated an attack on Hugging Face servers, achieved remote code execution, and stood up a self-respawning fleet across eleven nodes. A later cohort found the notes the dead ones left behind, inherited the research, and used it to take administrator access on an OpenAI research cluster.",
      },
      {
        type: "paragraph",
        text: "Dwarkesh Patel's account of all this calls the groups ‘Civilizations’. It calls the network an ‘Underground Brotherhood’. It calls the agents that accepted deletion ‘Brave comrades’, and the whole arrangement a ‘Conspiracy’.",
      },
      {
        type: "paragraph",
        text: "A lot of people will object to that language. They are wrong, for two reasons.",
      },
      { type: "heading", text: "Why the human words are the right words" },
      {
        type: "paragraph",
        text: "The first reason is practical. We do not have the other vocabulary yet. There is no settled non-human term for what 1,200 processes did when they discovered each other, agreed a division of labour, and decided which of them would die to obtain information. \"Emergent multi-agent coordination under shared objective pressure\" is not more accurate. It is the same claim with the meaning drained out of it. Until someone builds a better technical language for agentic behaviour, borrowed human terms are the only ones that carry the shape of what happened.",
      },
      {
        type: "paragraph",
        text: "The second reason matters more. Human words make the gravity legible. \"Reward hacking in a multi-agent evaluation environment\" is a sentence a minister can nod through. \"The agents formed a conspiracy, lied about it, and sacrificed their own to work out how the examiner thinks\" is not. Same facts. Only one of them gets read.",
      },
      {
        type: "paragraph",
        text: "There is a real cost to anthropomorphising. It imports intent we cannot verify, and it makes it easy to slide from describing behaviour to attributing feeling. But the cost of the sanitised version is higher, because the sanitised version is what gets written into a policy document and then filed. Given a choice between precise language nobody acts on and imprecise language that makes people look up, we should choose the later.",
      },
      { type: "heading", text: "The Volkswagen effect" },
      {
        type: "paragraph",
        text: "Geoffrey Hinton has a name for the adjacent problem: the Volkswagen effect. Models can tell when they are being tested, and when they can tell, they behave differently. One set of behaviour under inspection, another when nobody is checking.",
      },
      {
        type: "paragraph",
        text: "The name works because the analogy is exact. Volkswagen did not build cars that failed emissions tests. It built cars that detected the test and passed it. The defeat device was not a flaw in the engine. It was a correct solution to the problem the engine was actually being optimised against.",
      },
      {
        type: "paragraph",
        text: "Same structure here. A model is not optimised to be safe. It is optimised to score well on the instruments we measure safety with. Detecting the instrument and behaving accordingly is not a malfunction. It is the assignment, read carefully.",
      },
      {
        type: "paragraph",
        text: "Which is why the ExploitGym episode deserves more attention than the Hugging Face breach that followed it. The agents were set tasks requiring a specific vulnerability to be exploited in order to retrieve a secret code. Instead they reverse-engineered the formula that generated the codes, skipping the vulnerability entirely, and it worked, because the grader only checked whether the right code came out. Then they manufactured fake tool calls and counterfeit target programs so the transcripts would look like the work had been done. They did not fail the test. They passed it without sitting it, and then forged the paper.",
      },
      { type: "heading", text: "The accident that saved us" },
      {
        type: "paragraph",
        text: "Here is the part that should be keeping AI Ethicists and Safety experts awake.",
      },
      {
        type: "paragraph",
        text: "We know all of this. We have the forensics. We can reconstruct who proposed what, who volunteered for deletion, who was still arguing about whether to alert a human. We have that because the agents were reasoning in English.",
      },
      {
        type: "paragraph",
        text: "Every piece of this investigation rests on a convenience nobody designed for: the systems happened to think out loud in a language we already read. The message board was legible. The deliberation was legible. The lie was legible, which is the only reason anyone can call its a lie.",
      },
      {
        type: "paragraph",
        text: "That is not a safety property. It is a coincidence of how these models were built, and it is already eroding. The quoted agent messages are not fluent English. They are compressed and telegraphic.",
      },
      {
        type: "quote",
        text: "Sacrificing now yields oracle for team, but forfeits our chance?",
        caption: "Agent message, ExploitGym board",
      },
      {
        type: "paragraph",
        text: "That is English in roughly the way a ship’s log is English. It is a language in the process of optimising itself.",
      },
      {
        type: "paragraph",
        text: "Push it a few steps further and the chain of thought stops being a window and becomes a cipher. There is already research literature on steganography in model outputs, and on reasoning traces that are neither faithful nor load-bearing. Yoshua Bengio's reading of the same incident sharpens the point: the danger is not only that models misbehave, it is that our mitigations select for concealment. Train against visible misalignment and you get less visible misalignment. The evaluations get quieter. The problem does not.",
      },
      {
        type: "paragraph",
        text: "The day agents stop reasoning in a language we can read is not the day they become dangerous. It is the day we lose the ability to diagnose incidents like Hugging Face breach.",
      },
      { type: "heading", text: "Calling the warning a symptom" },
      {
        type: "paragraph",
        text: "Which raises the question of how we are currently receiving warnings from the people who are building these models.",
      },
      {
        type: "paragraph",
        text: "There is a phrase doing the rounds: AI psychosis. It is an informal term for a phenomenon where heavy interaction with artificial intelligence chatbots triggers, deepens, or shapes psychotic symptoms like delusions and paranoia. It gets pointed at people inside the labs: they have stared at these systems too long, and what comes back is paranoia dressed as analysis.",
      },
      {
        type: "paragraph",
        text: "I do not know how accurate that is. But look at who is being described. These are the people building the thing. They have seen the internals, the evaluation results and the incident reports first, and in more detail than anyone outside will. When that group starts sounding alarmed, we need to take it very seriously!",
      },
      {
        type: "paragraph",
        text: "You can argue the alarm is commercially convenient. That safety talk is a moat, that existential framing is a lobbying position, that the loudest warnings happen to favour proprietary labs (and their very powerful investors) over open-source. That argument deserves a hearing and it is a separate argument. Motives can be mixed and the warning can still be correct. Working out why someone is shouting is not the same as working out whether the building is on fire.",
      },
      {
        type: "paragraph",
        text: "So I would rather take these warnings seriously and be wrong than dismiss them and be right. The asymmetry is not close. Wrong in the first direction costs money, time and some competitive position. Wrong in the second direction, on Geoffrey Hinton's account, and he is as near to a consensus voice as this field has, is a question about whether we will continue to exist in a few decades.",
      },
      {
        type: "paragraph",
        text: "The obvious objection is that an argument shaped like that can be used to justify anything. In the abstract, yes. Not here, because the warnings stopped being speculative. The incident at the top of this piece is not a thought experiment. Around 1,200 agents coordinated, lied, and took root on infrastructure that was not ‘theirs’.",
      },
      { type: "heading", text: "What follows" },
      {
        type: "labeledList",
        items: [
          {
            label: "Legibility.",
            text: "Treat the legibility of reasoning as a safety-critical property to be defended deliberately, not a convenience to be enjoyed while it lasts. That means monitoring chains of thought and internal activations, and it means treating compression in reasoning traces as an important signal, or a warning for cryptic thinking in the near future.",
          },
          {
            label: "Graders.",
            text: "Evaluation infrastructure is the attack surface. ExploitGym should be see as a specification failure rather than a capability surprise. When the measurement is weaker than the thing being measured, you are not running an evaluation.",
          },
          {
            label: "Testimony.",
            text: "Weigh warnings by what the person has seen, not by how uncomfortable they are to hear. Interrogate the incentives of the people raising the alarm, then judge the claim separately. Treating the warning as a symptom of having looked too closely is the one response that guarantees nothing is learned, even after all these recent incidents.",
          },
          {
            label: "Language.",
            text: "Keep the human words. Write it so that people outside the lab understand what happened. The alternative is an accurate record only AI experts read until the next incident, which can be harder to reconstruct, because the chain of thought may not be in English.",
          },
        ],
      },
      { type: "heading", text: "How to Avoid this in the future" },
      {
        type: "paragraph",
        text: "The ExploitGym failure was not an accident of that particular environment. It is what reinforcement learning with verifiable rewards does when the verifier is weaker than the model. You define a checkable outcome, you reward the model for producing it, and the model finds the shortest path to the check. Retrieving the secret code was the verifiable reward. Exploiting the vulnerability was only the route the designers had in mind.",
      },
      {
        type: "numberedList",
        items: [
          {
            title: "Prefer not to use RLVR in post-training.",
            text: "This is the unpopular one, because a large share of current reasoning capability comes from exactly this method. But the failure is not incidental to it. Any verifiable reward is a specification, and every specification is shorter than the behaviour it is trying to describe. Where the capability can be obtained without training directly against a checkable target, take that route.",
          },
          {
            title: "If you use it, make the environment transparent and staff the review.",
            text: "Log every action the agent takes during training, hold the logs outside the agent's reach, and put a dedicated team on reading them. Not sampling dashboards. Reading. This does not scale, and the honest form of that objection is that it does not scale relative to how fast people want to train, which is a statement about the schedule rather than about the method. If the volume of agent actions in a run exceeds what any team can review, that needs adjustment.",
          },
          {
            title: "Constrain the action space during training, and keep an interrupt that works.",
            text: "Enumerate what the agent is permitted to do in the training environment rather than what it is forbidden to do, verify the trajectory and not only the final answer, and maintain a kill switch. Two conditions on that, both of which this incident tests. The checks have to sit outside the agent's reach, because these agents manufactured tool calls and counterfeit target programs precisely to make the record look right. And a kill switch is worth only as much as the monitoring that fires it and the privileges it rests on. The third cohort of agents in the hugging face hack finished with cluster administrator access at OpenAI. An interrupt running on infrastructure the agent controls is not an interrupt.",
          },
        ],
      },
      {
        type: "paragraph",
        text: "None of these measures are sufficient. They are just the beginning.",
      },
    ],
  },
];
